import {
  Avatar,
  Box,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { InfiniteHitsProvided } from 'react-instantsearch-core'
import {
  Configure,
  connectInfiniteHits,
  InstantSearch,
} from 'react-instantsearch-dom'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  Fade,
  IconButton,
  Icons,
  Image,
  PointDisplay,
  TextLink,
} from 'utoniq-ui'

import {
  Edge,
  EntryCampaignRanking,
  FeedConnectionFragment,
  FollowArtistGachasConnectionFragment,
  FollowArtistMissionsConnectionFragment,
  Gacha,
  GlobalEventFragment,
  GlobalHomeOrganizationArtistRankingsConnectionFragment,
  Maybe,
  Organization,
  OrganizationBanner,
  OrganizationNotificationBoxFragment,
  OrganizationNotificationConnectionFragment,
  PrepaidPointBalance,
  RankingPeriodType,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { SideMenu, SideMenuProps } from '@/components/layouts/SideMenu'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  ArtistBoxSkeleton,
  CampaignBoxSkeleton,
  CommentBox,
  EventCard,
  GachaBox,
  GachaBoxSkeleton,
  MissionBox,
  MissionBoxProps,
  MissionBoxSkeleton,
  PostBox,
  PostBoxSkeleton,
  RankListItem,
  RankListItemSkeleton,
} from '@/components/organisms'
import { HorizontalList } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import {
  Cell,
  CellFrame,
  Organism,
  OrganismFrame,
  Template,
} from '@/core/architecture'
import { individualHideWallet } from '@/core/individual'
import * as algolia from '@/lib/algolia'
import { isExternalLink, replaceParameters } from '@/lib/url'

const client = algolia.client()

export * from './fragments'

export type State = {
  loading: boolean
  isLoggedIn: boolean
  banners: Maybe<Organization['banners']>
  prepaidPointBalance: Maybe<
    Pick<PrepaidPointBalance, 'prepaidPointId' | 'balance'>
  >
  entryCampaignRankings: Organization['entryCampaignRankings']
  feeds: Maybe<FeedConnectionFragment>
  followArtistMissions: Maybe<FollowArtistMissionsConnectionFragment>
  followArtistGachas: Maybe<FollowArtistGachasConnectionFragment>
  organizationPatrons: SideMenuProps['memberArtists']
  organizationFollowees: SideMenuProps['followArtists']
  user: SideMenuProps['user']
  isArtist: boolean
  blockedUsers: string[]
  topDisplayArtistIds?: Maybe<Organization['topDisplayArtistIds']>
  notifications: Maybe<OrganizationNotificationConnectionFragment['edges']>
  organizationArtistRankings: Maybe<
    GlobalHomeOrganizationArtistRankingsConnectionFragment['edges']
  >
  events: Maybe<{ edges: { node: GlobalEventFragment }[] }>
}

export type Action = {
  onFilterFeeds: () => void
  onFetchMoreFeeds: () => void
  onLikePost: (
    postId: string,
    isLike: boolean,
    likeCount: number
  ) => Promise<void>
  onLikeComment: (
    commentId: string,
    isLike: boolean,
    likeCount: number
  ) => Promise<void>
}

type BannersCellProps = Pick<State, 'banners' | 'loading' | 'user'>

type HeaderIconCellProps = Pick<
  State,
  | 'user'
  | 'isArtist'
  | 'organizationFollowees'
  | 'organizationPatrons'
  | 'prepaidPointBalance'
  | 'loading'
>

type NotificationListsCellProps = {
  notifications: OrganizationNotificationConnectionFragment['edges']
}
type OrganizationArtistRankingCellProps = {
  organizationArtistRankings: State['organizationArtistRankings']
  loading: State['loading']
  isLoggedIn: boolean
}

type RankingCellProps = {
  entryCampaignRankings: Array<Maybe<EntryCampaignRanking>>
  loading: State['loading']
  isLoggedIn: boolean
}

type NewGachasCellProps = {
  followArtistGachas: State['followArtistGachas']
  loading: State['loading']
  isLoggedIn: boolean
}

type NewEventsCellProps = {
  events: State['events']
  loading: State['loading']
}

type NewMissionsCellProps = {
  followArtistMissions: State['followArtistMissions']
  loading: State['loading']
  isLoggedIn: boolean
}

type EdgeGachas = Array<
  Edge & {
    node: Pick<
      Gacha,
      'name' | 'id' | 'formattedEndAt' | 'thumbnail' | 'artistId' | 'gpsData'
    >
  }
>

type EdgeMission = Array<
  Edge & {
    node: MissionBoxProps['mission']
  }
>

export const GlobalHomeTemplate: Template<State, Action> = ({
  state,
  onFetchMoreFeeds,
  onFilterFeeds,
  onLikePost,
  onLikeComment,
}) => {
  const { routerNames } = useRouter()

  return (
    <BaseLayout
      navigation={{
        type: 'global',
        props: {
          hasManagementTask: !!state.user?.hasManagementTask,
          unreadNotificationCount: state.user?.unreadNotificationCount,
          hasSettingTask: !!state.user?.isMembershipPersonalInformationRequired,
        },
      }}
    >
      <Head title={routerNames.GlobalHome} />
      <HeaderIconCell
        loading={state.loading}
        isArtist={state.isArtist}
        organizationFollowees={state.organizationFollowees}
        organizationPatrons={state.organizationPatrons}
        user={state.user}
        prepaidPointBalance={state.prepaidPointBalance}
      />
      <BannersCell
        banners={state.banners}
        loading={state.loading}
        user={state.user}
      />
      {!!state.notifications?.length && (
        <NotificationListsCell notifications={state.notifications} />
      )}
      {state.loading ? (
        <ArtistsCell loading artists={[]} />
      ) : (
        <InstantSearch
          indexName={algolia.indexes.organizationArtists}
          searchClient={client}
        >
          <Configure
            filters={algolia.filters.topDisplayArtists(
              state.topDisplayArtistIds
            )}
          />
          <ArtistsCellWithAlgolia
            loading={state.loading}
            blockedUsers={state.blockedUsers}
            topDisplayArtistIds={state.topDisplayArtistIds}
          />
        </InstantSearch>
      )}
      {state.isLoggedIn && (
        <OrganizationArtistRankingCell
          loading={state.loading}
          organizationArtistRankings={state.organizationArtistRankings ?? []}
          isLoggedIn={state.isLoggedIn}
        />
      )}
      {state.isLoggedIn && (
        <RankingCell
          entryCampaignRankings={state.entryCampaignRankings ?? []}
          loading={state.loading}
          isLoggedIn={state.isLoggedIn}
        />
      )}
      {state.isLoggedIn && (
        <NewMissionsCell
          followArtistMissions={state.followArtistMissions}
          loading={state.loading}
          isLoggedIn={state.isLoggedIn}
        />
      )}
      {state.isLoggedIn && (
        <NewGachasCell
          followArtistGachas={state.followArtistGachas}
          loading={state.loading}
          isLoggedIn={state.isLoggedIn}
        />
      )}
      <NewEventsCell events={state.events} loading={state.loading} />
      {state.isLoggedIn && (
        <FeedScrollListOrganism
          feeds={state.feeds}
          isLoggedIn={state.isLoggedIn}
          loading={state.loading}
          isMembershipPersonalInformationRequired={
            !!state.user?.isMembershipPersonalInformationRequired
          }
          onFilter={onFilterFeeds}
          onLoadMore={onFetchMoreFeeds}
          onLikePost={onLikePost}
          onLikeComment={onLikeComment}
        />
      )}
    </BaseLayout>
  )
}

const BannersCell: Cell<BannersCellProps> = ({ banners, loading, user }) => {
  const isMultiple = !!banners && banners?.length > 1

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          width: '90vw',
          maxWidth: '600px',
          margin: '8px auto 0',
          borderRadius: 2,
          height: 'calc(90vw * 184 / 335)',
          maxHeight: 'calc(540px * 184 / 335)',
        }}
      />
    )
  }

  return (
    <CellFrame
      sx={{
        mt: 2,
        '.swiper-pagination-bullet-active': {
          bgcolor: 'grey.700',
        },
      }}
    >
      {banners?.length && (
        <Swiper
          pagination={{ clickable: true }}
          modules={[Pagination]}
          loop={isMultiple}
          slidesPerView={1.1}
          centeredSlides
          spaceBetween={10}
          enabled={isMultiple}
          style={{
            paddingBottom: isMultiple ? 40 : 0,
          }}
        >
          {banners.map((banner, index) => {
            return (
              <SwiperSlide key={index}>
                <BannerCell
                  thumbnail={banner.thumbnail}
                  link={banner.link}
                  text={banner.text}
                  user={user}
                />
              </SwiperSlide>
            )
          })}
        </Swiper>
      )}
    </CellFrame>
  )
}

const HeaderIconCell: Cell<HeaderIconCellProps> = ({
  organizationFollowees,
  organizationPatrons,
  user,
  isArtist,
  prepaidPointBalance,
  loading,
}) => {
  const { routerPages, generatePath } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)

  if (loading) {
    return null
  }
  return (
    <CellFrame
      display="flex"
      flexDirection="row"
      alignItems="center"
      sx={{ px: 5, pt: 4, pb: 2 }}
    >
      <IconButton.MyPage
        sx={{
          width: 44,
          height: 44,
          color: 'grey.600',
          mr: 3,
          position: 'relative',
          backgroundColor: 'grey.50',
          svg: {
            color: 'icon.line',
          },
        }}
        onClick={() => setOpenSideMenu(!openSideMenu)}
        badge={
          user?.hasManagementTask ||
          !!user?.unreadNotificationCount ||
          !!user?.isMembershipPersonalInformationRequired
            ? {
                badgeContent: '',
                color: 'like' as any,
                sx: {
                  '& > span': {
                    top: -4,
                    right: -4,
                    minWidth: 12,
                    height: 12,
                  },
                },
              }
            : undefined
        }
      />
      <SideMenu
        memberArtists={organizationPatrons}
        followArtists={organizationFollowees}
        open={openSideMenu}
        onClose={() => setOpenSideMenu(false)}
        user={user}
        isArtist={isArtist}
      />
      <Box display="flex" flexDirection="row" alignItems="center" flex={1}>
        {!!config.modules.prepaidPoint && !individualHideWallet() && (
          <PointDisplay point={prepaidPointBalance?.balance ?? 0} />
        )}
      </Box>
      {config.modules.campaign &&
        config.modules.mission &&
        // Liella!の場合はイベント導線非表示
        config.applicationKey !== 'liella' && (
          <IconButton.Event
            href={generatePath(routerPages.GlobalEvents)}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: 'grey.50',
              color: 'grey.600',
              mr: 3,
            }}
          />
        )}
      <IconButton.Notice
        href={routerPages.Notifications}
        sx={{
          width: 44,
          height: 44,
          backgroundColor: 'grey.50',
          color: 'grey.600',
        }}
      />
    </CellFrame>
  )
}

const NotificationListsCell: Cell<NotificationListsCellProps> = ({
  notifications,
}) => {
  const { t } = useTranslation('globalHome')
  const { routerPages } = useRouter()
  return (
    <CellFrame sx={{ px: 5, pt: 4, pb: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2 }}
      >
        <Typography variant="h3">{t('officialNotificationLabel')}</Typography>
        <Link variant="h6" href={routerPages.OfficialInformation}>
          {t('toList')}
        </Link>
      </Stack>
      <List>
        {notifications?.map(({ node }, index) => (
          <NotificationItemCell
            key={node.id}
            notification={node}
            divider={notifications?.length - 1 > index}
            isExternalLink={node.link ? isExternalLink(node.link) : false}
          />
        ))}
      </List>
    </CellFrame>
  )
}

type NotificationItemCellProps = {
  notification: OrganizationNotificationBoxFragment
  divider: boolean
  isExternalLink: boolean
}
const NotificationItemCell: Cell<NotificationItemCellProps> = ({
  notification,
  divider,
  isExternalLink,
}) => {
  const { generatePath, routerPages } = useRouter()
  const { onClick } = useBrowserLink(notification.link ?? '')

  return (
    <ListItemButton
      component={isExternalLink ? 'p' : Link}
      href={
        notification.link ||
        generatePath(routerPages.OfficialInformationDetail, {
          notificationId: notification.id,
        })
      }
      onClick={isExternalLink ? onClick : undefined}
      sx={{ height: 'auto', py: 3, px: 0 }}
      divider={divider}
    >
      <ListItemText
        primary={notification.title}
        primaryTypographyProps={{
          variant: 'body2',
          sx: styleSets.lineCamp(2),
        }}
      />
      <ListItemIcon>
        <Icons.Transition fontSize="xSmall" />
      </ListItemIcon>
    </ListItemButton>
  )
}

/**
 * @todo
 * [UT-3167] Algoliaの検索ロジック切り分け追加時に外す
 * https://app.clickup.com/t/908127/UT-3167
 */
const ArtistsCellWithAlgolia = connectInfiniteHits<
  InfiniteHitsProvided & {
    loading: boolean
    blockedUsers: string[]
    topDisplayArtistIds: State['topDisplayArtistIds']
  },
  {}
>(({ hits, loading, blockedUsers, topDisplayArtistIds }) => {
  if (['machizero'].includes(config.applicationKey)) return null

  /**
   * @description
   * algoliaへの依存を避けるために、objectID→idへ変数名を変更
   */

  const artists = hits
    .filter(artist => blockedUsers.indexOf(artist.objectID) < 0)
    .map(artist => ({ ...artist, id: artist.objectID }))

  if (!topDisplayArtistIds?.length)
    return <ArtistsCell artists={artists} loading={loading} />

  /**
   * topDisplayArtistIdsの配列昇順に表示する
   */
  const artistOrderByTopDisplayArtistId = topDisplayArtistIds
    .map(artistId => artists.find(({ id }) => id === artistId))
    .filter(v => !!v)

  return (
    <ArtistsCell artists={artistOrderByTopDisplayArtistId} loading={loading} />
  )
})

type Artist = {
  id: string
  displayName: string
  accountId: string
  formattedAccountId: string
  avatar: {
    uri: string
    smallUri?: string
  }
}
const ArtistsCell: Cell<{
  artists: Artist[]
  loading: State['loading']
}> = ({ artists, loading }) => {
  const { t } = useTranslation('globalHome')
  const { router, routerPages, generatePath } = useRouter()

  if (['machizero'].includes(config.applicationKey) && loading) return null

  return (
    <HorizontalList
      sx={{ pt: 5 }}
      loading={loading}
      title={t('artistLabel')}
      items={artists}
      loadingItemLength={8}
      LoadingComponent={ArtistBoxSkeleton}
      renderItem={(artist, i) => (
        <Fade key={i}>
          <ListItem
            sx={{
              display: 'inline-block',
              textAlign: 'center',
              p: 0,
              width: 64,
              borderRadius: 1,
            }}
            button
            onClick={() =>
              router.push(
                generatePath(routerPages.ArtistTop, {
                  artistId: artist.id,
                })
              )
            }
          >
            <Avatar
              sx={{ width: 64, height: 64 }}
              alt={artist.displayName}
              src={artist.avatar?.smallUri ?? artist.avatar?.uri}
            />
            <Typography
              variant="overline"
              sx={{ pt: 3, display: 'block', ...styleSets.lineCamp(1) }}
              color="text.secondary"
            >
              {artist.displayName}
            </Typography>
          </ListItem>
        </Fade>
      )}
    />
  )
}

const OrganizationArtistRankingCell: Cell<OrganizationArtistRankingCellProps> =
  ({ organizationArtistRankings, loading, isLoggedIn }) => {
    const { t } = useTranslation('globalHome')
    const { generatePath, routerPages } = useRouter()

    const showableRankings = organizationArtistRankings?.filter(edge => {
      const ranking = edge?.node

      return (
        ranking &&
        ranking.latestRankingPeriod?.periodType === RankingPeriodType.Monthly
      )
    })

    if (!showableRankings?.length || !isLoggedIn) return null

    return (
      <CellFrame>
        {loading ? (
          [1, 2, 3].map(i => (
            <Box key={i}>
              <Stack sx={{ px: 5, pb: 2 }}>
                <RankListItemSkeleton />
              </Stack>
            </Box>
          ))
        ) : showableRankings?.length ? (
          <Box sx={{ px: 5, pb: 6 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h3" sx={{ py: 2 }}>
                {t('organizationArtistRankingTitle')}
              </Typography>
              <TextLink
                variant="h5"
                underline="none"
                sx={{ color: 'primary.main' }}
                href={generatePath(routerPages.MyRanking)}
              >
                {t('toList')}
              </TextLink>
            </Stack>
            <List>
              {showableRankings.map((edge, index) => {
                const ranking = edge?.node
                return (
                  <Fade key={index}>
                    <Box sx={{ pt: 2 }}>
                      <RankListItem
                        key={index}
                        displayName={ranking?.artist?.displayName ?? ''}
                        avatarUrl={ranking?.artist?.avatar?.smallUri ?? ''}
                        point={
                          ranking?.latestRankingPeriod?.myRanking?.point ?? 0
                        }
                        rank={
                          ranking?.latestRankingPeriod?.myRanking?.rank ?? 0
                        }
                        userId={ranking?.artistId}
                      />
                    </Box>
                  </Fade>
                )
              })}
            </List>
          </Box>
        ) : null}
      </CellFrame>
    )
  }

const RankingCell: Cell<RankingCellProps> = ({
  entryCampaignRankings,
  loading,
  isLoggedIn,
}) => {
  const { t } = useTranslation('globalHome')
  const { generatePath, routerPages } = useRouter()

  if (!isLoggedIn || !entryCampaignRankings?.length || !config.modules.campaign)
    return null

  return (
    <CellFrame>
      {loading ? (
        [1, 2, 3].map(i => (
          <Box
            key={i}
            sx={{
              backgroundColor: 'grey.50',
            }}
          >
            <Stack sx={{ px: 5, pt: 5 }}>
              <CampaignBoxSkeleton />
            </Stack>
          </Box>
        ))
      ) : entryCampaignRankings?.length ? (
        <Box
          sx={{
            backgroundColor: 'grey.50',
            pt: 3,
            pb: 5,
            mb: 6,
          }}
        >
          <Typography variant="h3" sx={{ px: 5, py: 2 }}>
            {t('rankingLabel')}
          </Typography>
          <List>
            {entryCampaignRankings.map(
              (ranking, index) =>
                ranking && (
                  <Fade key={index}>
                    <ListItemButton
                      href={generatePath(routerPages.CampaignDetail, {
                        campaignId: ranking.id,
                      })}
                      sx={{
                        p: 0,
                        mx: 4,
                        my: 2,
                      }}
                    >
                      <Avatar
                        variant="square"
                        sx={{
                          width: 100,
                          height: 64,
                          borderRadius: 2,
                        }}
                        alt={ranking.myRank.displayName}
                        src={ranking.thumbnail?.smallUri || ''}
                      />
                      <Box
                        sx={{ pl: 4, width: '100%' }}
                        display="flex"
                        flexDirection="column"
                      >
                        <Typography variant="h2" sx={{ pb: 1 }}>
                          {ranking.myRank.rank}
                          <Typography
                            component="p"
                            variant="subtitle2"
                            display="inline"
                          >
                            {t('rank')}
                          </Typography>
                        </Typography>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-end"
                        >
                          <Typography variant="body1">
                            {ranking.myRank.displayName}
                          </Typography>
                          <Typography variant="body2" color="text.hint">
                            {ranking.myRank.point}pt
                          </Typography>
                        </Box>
                      </Box>
                    </ListItemButton>
                  </Fade>
                )
            )}
          </List>
        </Box>
      ) : null}
    </CellFrame>
  )
}

const NewMissionsCell: Cell<NewMissionsCellProps> = ({
  followArtistMissions,
  loading,
  isLoggedIn,
}) => {
  const { t } = useTranslation('globalHome')
  const { analytics } = useAnalytics()
  const newMissions = followArtistMissions?.edges as EdgeMission
  const { routerPages, generatePath } = useRouter()

  if (!isLoggedIn || !newMissions?.length || !config.modules.mission)
    return null

  return (
    <HorizontalList
      loading={loading}
      title={t('newMissionLabel')}
      items={newMissions}
      trans={t('toList')}
      LoadingComponent={MissionBoxSkeleton}
      listHref={generatePath(routerPages.GlobalEvents, {
        tab: 'mission',
      })}
      renderItem={(mission, i) => (
        <Fade key={i}>
          <MissionBox
            sx={{ mt: 0 }}
            mission={mission.node}
            onClick={() => {
              analytics?.track('click_select', {
                applicationKey: config.applicationKey,
                artistId: mission.node?.artist.id,
                missionId: mission.node?.id,
                type: mission.node?.type,
                name: 'missions',
              })
            }}
          />
        </Fade>
      )}
      EmptyComponent={<EmptyCell message={t('newMissionEmptyMessage')} />}
    />
  )
}

const NewGachasCell: Cell<NewGachasCellProps> = ({
  followArtistGachas,
  loading,
  isLoggedIn,
}) => {
  const { t } = useTranslation('globalHome')
  // @ts-ignore
  const gachas = followArtistGachas?.edges as EdgeGachas

  if (!isLoggedIn || !gachas?.length || !config.modules.gacha) return null

  return (
    <HorizontalList
      loading={loading}
      title={t('newGachasLabel')}
      items={gachas}
      LoadingComponent={GachaBoxSkeleton}
      renderItem={(gacha, i) => (
        <Fade key={i}>
          <GachaBox gacha={gacha.node} artistId={gacha.node.artistId} />
        </Fade>
      )}
      EmptyComponent={<EmptyCell message={t('newGachasEmptyMessage')} />}
    />
  )
}

const NewEventsCell: Cell<NewEventsCellProps> = ({ events, loading }) => {
  const { t } = useTranslation('globalHome')
  const { routerPages } = useRouter()

  if (!events?.edges?.length || !config.modules.event) return null

  return (
    <HorizontalList
      loading={loading}
      title={t('newEventsLabel')}
      listHref={routerPages.ShokufesEvents}
      items={events.edges}
      trans={t('toList')}
      spacing={4}
      LoadingComponent={GachaBoxSkeleton}
      renderItem={(event, i) => (
        <Fade key={i}>
          <EventCard event={event.node} sx={{ width: 315, height: '100%' }} />
        </Fade>
      )}
    />
  )
}

const FeedScrollListOrganism: Organism<{
  feeds: State['feeds']
  loading: State['loading']
  isLoggedIn: boolean
  isMembershipPersonalInformationRequired: boolean
  onLoadMore: (e: MouseEvent) => void
  onFilter: Action['onFilterFeeds']
  onLikePost: Action['onLikePost']
  onLikeComment: Action['onLikeComment']
}> = ({
  feeds,
  isLoggedIn,
  onLikePost,
  onLikeComment,
  loading,
  isMembershipPersonalInformationRequired,
}) => {
  const { t } = useTranslation('globalHome')

  if (!isLoggedIn || ['machizero'].includes(config.applicationKey)) return null

  return (
    <OrganismFrame sx={{ backgroundColor: 'background.default', pb: 7 }}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pl: 5, pr: '10px' }}
      >
        <Typography variant="h3">{t('feedLabel')}</Typography>
        {/**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-4292
         */}
        {/*<IconButton onClick={onFilter} sx={{ color: 'text.secondary', mr: -2 }}>*/}
        {/*  <Icons.NarrowDown fontSize="medium" />*/}
        {/*</IconButton>*/}
      </Box>
      <Stack spacing={3} sx={{ px: 5, mt: 3 }}>
        {loading ? (
          <>
            {[1, 2, 3].map(i => (
              <PostBoxSkeleton fullWidth key={i} />
            ))}
          </>
        ) : feeds?.edges?.length ? (
          feeds?.edges.map(({ node }) => {
            if (!(node && node.data)) {
              return null
            }
            if ('resourceType' in node.data) {
              return (
                <Fade key={node.id}>
                  <PostBox
                    onLike={() => {
                      if (node && node.data && 'resourceType' in node.data) {
                        const { id, isLike, likeCount } = node.data
                        onLikePost(id, isLike, likeCount)
                      }
                    }}
                    post={node.data}
                    isMembershipPersonalInformationRequired={
                      isMembershipPersonalInformationRequired
                    }
                    fullWidth
                  />
                </Fade>
              )
            }
            return (
              <Fade key={node.id}>
                <CommentBox
                  likeComment={() => {
                    node.data &&
                      onLikeComment(
                        node.data.id,
                        node.data.isLike,
                        node.data.likeCount
                      )
                  }}
                  comment={node.data}
                  fullWidth
                />
              </Fade>
            )
          })
        ) : (
          <EmptyCell message={t('feedEmptyMessage')} />
        )}
      </Stack>
    </OrganismFrame>
  )
}

export const EmptyCell: Cell<{ message: string }> = ({ message }) => {
  return (
    <CellFrame sx={{ px: 6, py: 5, textAlign: 'center' }}>
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
    </CellFrame>
  )
}

const BannerCell: Cell<
  Omit<OrganizationBanner, 'order'> & { user: SideMenuProps['user'] }
> = ({ thumbnail, text, link, user }) => {
  const isExternal = link?.externalUrl
  const href = isExternal
    ? link?.externalUrl ?? ''
    : link?.innerPath ?? ''
    ? link?.innerPath ?? ''
    : ''

  const replacedUrl = replaceParameters(href, {
    uid: user?.id ?? '',
  })

  const bannerStyle = {
    display: 'block',
  }
  const imgStyle = {
    display: 'block',
    width: '100%',
    maxWidth: '600px',
  }

  const bannerSrc = thumbnail.mediumUri ?? (thumbnail.uri || '')

  if (isExternal)
    return (
      <a
        style={bannerStyle}
        href={replacedUrl}
        target="_blank"
        rel="noreferrer noopener"
      >
        <Image style={imgStyle} src={bannerSrc} alt={text} />
      </a>
    )

  return (
    <Link sx={bannerStyle} href={replacedUrl}>
      <Image style={imgStyle} src={bannerSrc} alt={text} />
    </Link>
  )
}
